<template>
  <qtm-content-block :collapsed="collapsed" collapsible title="Order Info">
    <v-row no-gutters>
      <v-col v-bind="cols">
        <qtm-input-label v-if="isStaff && !noOwner" label="Order Owner" required>
          <user-search
            placeholder="Search for an owner for this order"
            :readonly="readonlyOwner"
            class="mb-4"
            clearable
            :disabled="readonlyOwner"
            :error-messages="!owner ? errors.owner : undefined"
            :model-value="owner"
            @update:model-value="$emit('update:owner', $event)"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col v-bind="cols">
        <qtm-input-label label="Order Name" required>
          <qtm-text-field
            class="mb-4"
            data-test="reference-name-input"
            :disabled="readonlyName"
            :error-messages="errors.referenceName"
            hide-details="auto"
            :maxlength="ORDER_NAME_MAX_LENGTH"
            :model-value="referenceName"
            placeholder="Reference name for this order"
            @update:model-value="$emit('update:reference-name', $event)"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col v-bind="cols">
        <qtm-input-label v-if="!inventory" label="Project" :required="jobsiteRequired">
          <jobsite-select
            :disabled="!!jobsite && lockJobsite"
            :error-messages="!jobsite ? errors.jobsite : undefined"
            :model-value="jobsite"
            :user="projectUser"
            :user-required="!noOwner"
            @update:model-value="$emit('update:jobsite', $event)"
          />
        </qtm-input-label>
        <qtm-checkbox
          v-if="(inventory || canManageInventory) && !noInventory"
          :disabled="(inventory || !!jobsite) && lockJobsite || !canManageInventory"
          :model-value="inventory"
          @update:model-value="updateInventory"
        >
          <template v-slot:label>
            <span class="text-secondary">
              Inventory Order
            </span>
          </template>
        </qtm-checkbox>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col v-bind="cols">
        <po-number-format-select
          ref="po-number-select"
          :model-value="poNumberFormat"
          :company="owner?.company?.id"
          :disabled="readonlyPoFormat"
          class="mt-4"
          @update:model-value="$emit('update:po-number-format', $event)"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col v-bind="cols">
        <slot />
      </v-col>
    </v-row>
  </qtm-content-block>
</template>

<script setup lang="ts">
import { required, requiredIf, maxLength } from '@vuelidate/validators'
import type { Jobsite, User } from '@quotetome/materials-api'
import { ORDER_NAME_MAX_LENGTH } from '@/constants'
import UserSearch from '@/components/users/user-search.vue'
import JobsiteSelect from '@/components/jobsites/jobsite-select.vue'
import PoNumberFormatSelect from '@/components/companies/po-number-format-select.vue'
import useValidation from '@/composables/validation'

export interface Props {
  collapsed?: boolean
  fullWidth?: boolean
  inventory?: boolean
  jobsite?: Jobsite | null
  jobsiteRequired?: boolean
  lockJobsite?: boolean
  noInventory?: boolean
  noOwner?: boolean
  owner?: User | null
  poNumberFormat?: number | null
  readonlyName?: boolean
  readonlyOwner?: boolean
  readonlyPoFormat?: boolean
  referenceName?: string
}

const props = withDefaults(defineProps<Props>(), {
  jobsite: null,
  jobsiteRequired: true,
  owner: null,
  poNumberFormat: null,
  referenceName: '',
})
const emit = defineEmits([
  'update:inventory',
  'update:jobsite',
  'update:owner',
  'update:reference-name',
  'update:po-number-format',
])

const authStore = useAuthStore()
const user = authStore.user
const isStaff = authStore.isStaff
const canManageInventory = authStore.canManageInventory

const { errors, isValid } = useValidation({
  errorsByKey: { referenceName: `This field is required, max length: ${ORDER_NAME_MAX_LENGTH} characters` },
  rules: {
    jobsite: { required: requiredIf(() => props.jobsiteRequired) },
    owner: { required: requiredIf(() => !!isStaff && !props.noOwner) },
    referenceName: { required, maxLength: maxLength(ORDER_NAME_MAX_LENGTH) },
  },
  state: toRefs(props),
})
const poNumberSelectRef = useTemplateRef('po-number-select')
const formIsValid = () => [poNumberSelectRef.value?.isValid() ?? true, isValid()].every(Boolean)

const cols = computed(() => {
  if (props.fullWidth) {
    return { cols: 12 }
  }

  return { cols: 12, sm: 8, lg: 6 }
})
const projectUser = computed(() => (isStaff ? props.owner : user) || undefined)
const updateInventory = (value: boolean) => {
  emit('update:inventory', value)
  emit('update:jobsite', null)
}

defineExpose({ isValid: formIsValid })
</script>
